import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, FileText, FileType, FileSpreadsheet, X } from 'lucide-react';
import { Button } from '../ui/button';
import { DocumentViewer } from '../playbooks/document-viewer/DocumentViewer';
import { SuggestionDocumentViewer } from './document-viewer';
import { ProjectFile, getFileDownloadUrl } from '../../services/Project';
import { ExportFileOptions, QAgentSuggestion, exportFileWithChanges } from '../../services/QAgent';
import { ExportDocumentMenu } from './suggestions';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';
import { startDownload } from '../../utils/download';

interface FileViewerPanelProps {
  file: ProjectFile | null;
  projectId: string;
  chatId?: string;
  isOpen: boolean;
  onClose: () => void;
  // Suggestion support
  suggestions?: QAgentSuggestion[];
  scrollToSuggestionId?: string | null;
  onScrollComplete?: () => void;
  onAcceptSuggestion?: (suggestionId: string) => void;
  onRejectSuggestion?: (suggestionId: string) => void;
  onUndoSuggestion?: (suggestionId: string) => void;
  onSuggestionsApplyFailed?: (suggestionIds: string[]) => void;
  highlightQuotes?: string[];
  scrollToQuote?: string | null;
  onQuoteScrollComplete?: () => void;
}

type FileType = 'pdf' | 'docx' | 'xlsx' | 'txt';

const getFileTypeFromMime = (mimeType: string, fileName: string): FileType | null => {
  if (mimeType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return 'pdf';
  }
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fileName.endsWith('.docx')
  ) {
    return 'docx';
  }
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    fileName.endsWith('.xlsx') ||
    fileName.endsWith('.xls')
  ) {
    return 'xlsx';
  }
  if (mimeType.startsWith('text/') || fileName.endsWith('.txt') || fileName.endsWith('.md')) {
    return 'txt';
  }
  return null;
};

const getFileIcon = (fileType: FileType | null) => {
  switch (fileType) {
    case 'pdf':
      return <FileText className="h-4 w-4 text-red-500" />;
    case 'docx':
    case 'txt':
      return <FileType className="h-4 w-4 text-blue-500" />;
    case 'xlsx':
      return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
    default:
      return <FileText className="h-4 w-4 text-muted-foreground" />;
  }
};

export const FileViewerSheet: React.FC<FileViewerPanelProps> = ({
  file,
  projectId,
  chatId,
  isOpen,
  onClose,
  suggestions = [],
  scrollToSuggestionId,
  onScrollComplete,
  onAcceptSuggestion,
  onRejectSuggestion,
  onUndoSuggestion,
  onSuggestionsApplyFailed,
  highlightQuotes,
  scrollToQuote,
  onQuoteScrollComplete,
}) => {
  const { t } = useI18n();
  const fv = t.qagentDocument.fileViewer;
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by Retry to reload the same file
  const [reloadKey, setReloadKey] = useState(0);

  const fileType = file ? getFileTypeFromMime(file.mimeType, file.name) : null;

  // Filter suggestions for this file
  const fileSuggestions = file
    ? suggestions.filter(s => s.fileId === file.id)
    : [];
  const hasSuggestions = fileSuggestions.length > 0;
  const hasAcceptedSuggestions = fileSuggestions.some(s => s.status === 'accepted');
  const highlightedSources = useMemo(
    () => (highlightQuotes ?? []).map(quote => ({ quote })),
    [highlightQuotes],
  );

  // Load file URL when file changes
  useEffect(() => {
    if (!file || !isOpen) {
      setFileUrl(null);
      setError(null);
      return;
    }

    const loadFile = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getFileDownloadUrl(projectId, file.id);
        if (response.success && response.data?.url) {
          const fileResponse = await fetch(response.data.url);
          if (!fileResponse.ok) {
            throw new Error('Failed to fetch file');
          }
          const blob = await fileResponse.blob();
          const blobUrl = URL.createObjectURL(blob);
          setFileUrl(blobUrl);
        } else {
          throw new Error('Failed to get download URL');
        }
      } catch (err) {
        console.error('Error loading file:', err);
        setError(fv.loadFailed);
        toast.error(fv.loadFailed);
      } finally {
        setIsLoading(false);
      }
    };

    loadFile();

    return () => {
      if (fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [file?.id, projectId, isOpen, reloadKey]);

  // With accepted suggestions: download them as a redline or a clean copy
  const handleExport = async (options: ExportFileOptions) => {
    if (!file || !chatId) return;
    setIsExporting(true);
    try {
      const response = await exportFileWithChanges(chatId, file.id, options);
      if (response.success && response.data?.downloadUrl) {
        startDownload(response.data.downloadUrl, response.data.fileName);
        const skipped = response.data.skipped?.length ?? 0;
        if (skipped > 0) toast.warning(fv.changesSkipped(skipped));
      } else {
        toast.error(response.errors?.[0]?.detail || fv.downloadFailed);
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error(fv.downloadFailed);
    } finally {
      setIsExporting(false);
    }
  };

  // Without accepted suggestions: the file as it is
  const handleDownload = async () => {
    if (!file) return;
    try {
      const response = await getFileDownloadUrl(projectId, file.id);
      if (response.success && response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (err) {
      toast.error(fv.downloadFailed);
    }
  };

  const exportsChanges = hasAcceptedSuggestions && !!chatId;
  const downloadButton = (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={exportsChanges ? undefined : handleDownload}
      disabled={isExporting}
      title={exportsChanges ? fv.downloadWithChanges : fv.download}
    >
      {isExporting ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
    </Button>
  );

  if (!isOpen) return null;

  return (
    <div className="h-full flex flex-col border-l bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b flex-shrink-0">
        {fileType && getFileIcon(fileType)}
        <span className="text-sm font-medium truncate flex-1">
          {file?.name || fv.documentFallback}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {exportsChanges && file ? (
            <ExportDocumentMenu fileName={file.name} onExport={handleExport}>
              {downloadButton}
            </ExportDocumentMenu>
          ) : (
            downloadButton
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onClose}
            title={fv.close}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm">{fv.loading}</span>
            </div>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <FileText className="h-8 w-8" />
              <span className="text-sm">{error}</span>
              <Button variant="outline" size="sm" onClick={() => setReloadKey(k => k + 1)}>
                {fv.retry}
              </Button>
            </div>
          </div>
        )}

        {!isLoading && !error && fileUrl && fileType && (
          <div className="h-full overflow-auto">
            {fileType === 'docx' && hasSuggestions && onAcceptSuggestion && onRejectSuggestion ? (
              <SuggestionDocumentViewer
                fileUrl={fileUrl}
                suggestions={fileSuggestions}
                scrollToSuggestionId={scrollToSuggestionId}
                onScrollComplete={onScrollComplete}
                onAcceptSuggestion={onAcceptSuggestion}
                onRejectSuggestion={onRejectSuggestion}
                onUndoSuggestion={onUndoSuggestion}
                onSuggestionsApplyFailed={onSuggestionsApplyFailed}
              />
            ) : (
              <DocumentViewer
                fileUrl={fileUrl}
                fileType={fileType}
                highlightedSources={highlightedSources}
                scrollToSource={scrollToQuote ? { quote: scrollToQuote } : null}
                onScrollComplete={onQuoteScrollComplete}
                exactMatch
              />
            )}
          </div>
        )}

        {!isLoading && !error && !fileType && file && (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <FileText className="h-8 w-8" />
              <span className="text-sm">{fv.unsupported}</span>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                {fv.downloadInstead}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileViewerSheet;
